import { useCallback, useEffect, useState } from "react"
import type { Dispatch, SetStateAction } from "react"
import axios from "axios"
import {
  getPermitDetail,
  getContractorWorkers,
  buildSubactivityFieldKey,
  type PermitDetail,
  type WorkerSelectionItem,
} from "modules/permit"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { WorkAreaSelection } from "modules/permit/components/WorkAreaSection"
import type { SearchSelectValue } from "modules/permit/components/SearchSelectField"
import type { PermitType, TemplateConfiguration } from "shared/api/templateService"
import {
  createEmptyRiskState,
  normaliseAreaList,
  normaliseGpsAreaList,
  normaliseMarkerValue,
  parseNullableNumber,
  toStringOrUndefined,
  type RiskState,
} from "modules/permit/utils/permitHelpers"
import {
  isSameWorkerDirectoryRecord,
  resolveWorkerCompanyName,
  resolveWorkerJobRole,
} from "modules/permit/utils/workerSelection"
import {
  GENERAL_FIELD_STATE_MAP,
  GENERAL_SECTION_CODE,
} from "modules/permit/utils/permitFieldMapping"
import { buildTemplateFieldLabelKey } from "modules/permit/components/permit-request/utils/templateFieldUtils"
import type { PermitFormState } from "../types"

const formatDateFromIso = (isoString?: string | null) => {
  if (!isoString) return ""
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return ""
  return date.toISOString().slice(0, 10)
}

const formatTimeFromIso = (isoString?: string | null) => {
  if (!isoString) return ""
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return ""
  const hours24 = date.getHours()
  const minutes = date.getMinutes()
  return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

const parseTimeValue = (value: string | null | undefined) => {
  if (!value) {
    return ""
  }
  const trimmed = value.trim()
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i)
  if (!match) {
    return trimmed
  }

  let hour = Number.parseInt(match[1] ?? "0", 10)
  const minute = Number.parseInt(match[2] ?? "0", 10)
  const period = match[3]?.toUpperCase() ?? null
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return trimmed
  }
  if (period === "PM" && hour < 12) {
    hour += 12
  } else if (period === "AM" && hour === 12) {
    hour = 0
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

const resolveWorkerSearchTerm = (worker: WorkerSelectionItem): string | null => {
  const cardNumber = worker.cardNumber?.trim()
  if (cardNumber) {
    return cardNumber
  }
  const fullName = worker.fullName?.trim()
  if (fullName) {
    return fullName
  }
  const username = worker.username?.trim()
  if (username) {
    return username
  }
  return null
}

const enrichWorkersWithImages = async (
  tenantId: string,
  workers: WorkerSelectionItem[],
): Promise<WorkerSelectionItem[]> => {
  const pending = workers.filter((worker) => !worker.imagePath || !worker.companyName?.trim())
  if (pending.length === 0) {
    return workers
  }

  const searchCache = new Map<string, ReturnType<typeof getContractorWorkers>>()

  const resolveDirectoryMatch = async (worker: WorkerSelectionItem) => {
    const searchTerm = resolveWorkerSearchTerm(worker)
    if (!searchTerm) {
      return worker
    }
    if (!searchCache.has(searchTerm)) {
      searchCache.set(
        searchTerm,
        getContractorWorkers({
          tenantId,
          search: searchTerm,
          pageNumber: 1,
          pageSize: 50,
        }),
      )
    }
    try {
      const response = await searchCache.get(searchTerm)!
      const matches = response.items?.filter((item) => isSameWorkerDirectoryRecord(worker, item)) ?? []
      if (matches.length === 0) {
        return worker
      }

      const preferredMatch =
        matches.find(
          (item) =>
            item.orgName?.trim() ||
            item.cardNumber?.trim() ||
            item.imagePath?.trim() ||
            item.username?.trim(),
        ) ?? matches[0]

      const resolvedCompanyName =
        matches
          .map((item) => resolveWorkerCompanyName(item))
          .find((value) => typeof value === "string" && value.trim().length > 0) ?? worker.companyName

      const resolvedOrgName =
        matches
          .map((item) => item.orgName?.trim())
          .find((value) => typeof value === "string" && value.length > 0) ?? worker.orgName

      const resolvedCardNumber =
        matches
          .map((item) => item.cardNumber?.trim())
          .find((value) => typeof value === "string" && value.length > 0) ?? worker.cardNumber

      return {
        ...worker,
        imageType: preferredMatch.imageType ?? worker.imageType,
        imagePath: preferredMatch.imagePath ?? worker.imagePath,
        username: preferredMatch.username ?? worker.username,
        orgName: resolvedOrgName,
        companyName: resolvedCompanyName,
        cardNumber: resolvedCardNumber,
        jobRole: worker.jobRole || resolveWorkerJobRole(preferredMatch),
      }
    } catch {
      return worker
    }
  }

  const updatedPending = await Promise.all(pending.map(resolveDirectoryMatch))
  const updatedMap = new Map(updatedPending.map((worker) => [worker.workerId, worker]))
  return workers.map((worker) => updatedMap.get(worker.workerId) ?? worker)
}

interface UsePermitDetailHydrationParams {
  tenantId: string
  mode: "create" | "edit"
  permitId?: string | null
  templateConfig: TemplateConfiguration | null
  permitTypes: PermitType[]
  currentUser: { name?: string; company?: string; companyId?: string | null }
  setApprovalStateId: (value: string | null) => void
  setPermitData: Dispatch<SetStateAction<PermitFormState>>
  setWorkAreaSelection: Dispatch<SetStateAction<WorkAreaSelection>>
  setSelectedActivities: Dispatch<SetStateAction<string[]>>
  setSelectedWorkers: Dispatch<SetStateAction<WorkerSelectionItem[]>>
  setExtraFieldValues: Dispatch<SetStateAction<Record<string, string>>>
  setUploadedFiles: Dispatch<SetStateAction<UploadedFileItem[]>>
  setRiskData: Dispatch<SetStateAction<RiskState>>
}

export interface RefreshPermitDetailOptions {
  hydrateForm?: boolean
}

interface UsePermitDetailHydrationResult {
  permitDetail: PermitDetail | null
  isPermitDetailLoading: boolean
  permitDetailError: string | null
  hasAppliedPermitDetail: boolean
  refreshPermitDetail: (options?: RefreshPermitDetailOptions) => void
  markDetailHydrationPending: () => void
  permitDetailReloadToken: number
}

export const usePermitDetailHydration = ({
  tenantId,
  mode,
  permitId,
  templateConfig,
  permitTypes,
  currentUser,
  setApprovalStateId,
  setPermitData,
  setWorkAreaSelection,
  setSelectedActivities,
  setSelectedWorkers,
  setExtraFieldValues,
  setUploadedFiles,
  setRiskData,
}: UsePermitDetailHydrationParams): UsePermitDetailHydrationResult => {
  const [permitDetail, setPermitDetail] = useState<PermitDetail | null>(null)
  const [isPermitDetailLoading, setIsPermitDetailLoading] = useState(false)
  const [permitDetailError, setPermitDetailError] = useState<string | null>(null)
  const [hasAppliedPermitDetail, setHasAppliedPermitDetail] = useState(false)
  const [shouldHydrateForm, setShouldHydrateForm] = useState(true)
  const [permitDetailReloadToken, setPermitDetailReloadToken] = useState(0)

  const refreshPermitDetail = useCallback((options?: RefreshPermitDetailOptions) => {
    setShouldHydrateForm(options?.hydrateForm ?? true)
    setPermitDetailReloadToken((token) => token + 1)
  }, [])

  const markDetailHydrationPending = useCallback(() => {
    setHasAppliedPermitDetail(false)
  }, [])

  useEffect(() => {
    setPermitData((prev) => ({
      ...prev,
      requestorName: currentUser.name ?? prev.requestorName,
      requestorCompany: currentUser.company ?? prev.requestorCompany,
    }))
  }, [currentUser.name, currentUser.company, setPermitData])

  useEffect(() => {
    if (mode !== "edit" || !permitDetail?.permitTypeCode) {
      return
    }

    setPermitData((prev) => {
      const nextPermitTypeCode = permitDetail.permitTypeCode ?? prev.permitTypeCode
      const nextPermitType =
        permitTypes.find((type) => type.permitCode === permitDetail.permitTypeCode)?.permitType ??
        permitDetail.permitTypeName ??
        prev.permitType
      const nextPermitNo = permitDetail.permitNumber ?? prev.permitNo

      if (
        prev.permitTypeCode === nextPermitTypeCode &&
        prev.permitType === nextPermitType &&
        prev.permitNo === nextPermitNo
      ) {
        return prev
      }

      return {
        ...prev,
        permitTypeCode: nextPermitTypeCode,
        permitType: nextPermitType,
        permitNo: nextPermitNo,
      }
    })
  }, [mode, permitDetail?.permitTypeCode, permitDetail?.permitTypeName, permitDetail?.permitNumber, permitTypes, setPermitData])

  useEffect(() => {
    if (mode !== "edit" || !permitId) {
      setPermitDetail(null)
      setPermitDetailError(null)
      setHasAppliedPermitDetail(false)
      setApprovalStateId(null)
      return
    }

    let cancelled = false
    setIsPermitDetailLoading(true)
    setPermitDetailError(null)
    if (shouldHydrateForm) {
      setHasAppliedPermitDetail(false)
    }

    getPermitDetail(tenantId, permitId)
      .then((detail) => {
        if (cancelled) return
        if (!detail) {
          setPermitDetailError("ไม่พบข้อมูลใบอนุญาต")
          setPermitDetail(null)
          setApprovalStateId(null)
          return
        }
        setPermitDetail(detail)
        setApprovalStateId(detail.approvalStateId ?? null)
      })
      .catch((error) => {
        if (cancelled) return
        console.error("Failed to load permit detail", error)
        if (axios.isAxiosError(error)) {
          const message =
            (error.response?.data as any)?.message ??
            error.response?.statusText ??
            error.message ??
            "ดาวน์โหลดรายละเอียดใบอนุญาตไม่สำเร็จ"
          setPermitDetailError(message)
        } else {
          setPermitDetailError("ดาวน์โหลดรายละเอียดใบอนุญาตไม่สำเร็จ")
        }
        setPermitDetail(null)
        setApprovalStateId(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsPermitDetailLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [mode, permitId, tenantId, setApprovalStateId, permitDetailReloadToken, shouldHydrateForm])

  useEffect(() => {
    if (
      mode !== "edit" ||
      !permitDetail ||
      !templateConfig ||
      hasAppliedPermitDetail ||
      templateConfig.permitCode !== permitDetail.permitTypeCode
    ) {
      return
    }

    const metadata = permitDetail.metadata ?? {}
    const detailWorkArea = permitDetail.workArea

    if (detailWorkArea) {
      const mapMode = detailWorkArea.mapMode === "gps" ? "gps" : "plot-plan"
      const gpsAreas =
        detailWorkArea.gpsAreas && detailWorkArea.gpsAreas.length > 0
          ? detailWorkArea.gpsAreas.map((item) => ({
              north: item.north,
              south: item.south,
              east: item.east,
              west: item.west,
            }))
          : normaliseGpsAreaList(metadata["workAreaGpsAreas"])
      const areas =
        detailWorkArea.areas && detailWorkArea.areas.length > 0
          ? detailWorkArea.areas.map((item) => ({
              xPercent: item.xPercent,
              yPercent: item.yPercent,
              widthPercent: item.widthPercent,
              heightPercent: item.heightPercent,
            }))
          : detailWorkArea.area
            ? [
                {
                  xPercent: detailWorkArea.area.xPercent,
                  yPercent: detailWorkArea.area.yPercent,
                  widthPercent: detailWorkArea.area.widthPercent,
                  heightPercent: detailWorkArea.area.heightPercent,
                },
              ]
            : []
      const lastArea = areas.length > 0 ? areas[areas.length - 1] : null
      setWorkAreaSelection({
        mapMode,
        plotPlanId: toStringOrUndefined(detailWorkArea.plotPlanId),
        plotPlanVersionId: toStringOrUndefined(detailWorkArea.plotPlanVersionId),
        plotPlanName: toStringOrUndefined(detailWorkArea.plotPlanName),
        plotPlanImagePath: toStringOrUndefined(detailWorkArea.plotPlanImagePath),
        marker:
          mapMode === "gps" || !detailWorkArea.marker
            ? null
            : {
                xPercent: detailWorkArea.marker.xPercent,
                yPercent: detailWorkArea.marker.yPercent,
              },
        area: lastArea,
        areas: areas.length > 0 ? areas : null,
        gpsLatitude: detailWorkArea.gpsLatitude ?? null,
        gpsLongtitude: detailWorkArea.gpsLongtitude ?? null,
        gpsAreas: gpsAreas.length > 0 ? gpsAreas : null,
      })
    } else {
      const marker = normaliseMarkerValue(metadata["workAreaMarker"])
      const areasFromList = normaliseAreaList(metadata["workAreaAreas"])
      const areasFromSingle = normaliseAreaList(metadata["workAreaArea"])
      const areas = areasFromList.length > 0 ? areasFromList : areasFromSingle
      const area = areas.length > 0 ? areas[areas.length - 1] : null

      const workAreaMapModeRaw =
        typeof metadata["workAreaMapMode"] === "string"
          ? metadata["workAreaMapMode"].toLowerCase()
          : ""

      const derivedWorkArea: WorkAreaSelection = {
        mapMode: workAreaMapModeRaw === "gps" ? "gps" : "plot-plan",
        plotPlanId: toStringOrUndefined(metadata["workAreaPlotPlanId"]),
        plotPlanVersionId: toStringOrUndefined(metadata["workAreaPlotPlanVersionId"]),
        plotPlanName: toStringOrUndefined(metadata["workAreaPlotPlanName"]),
        plotPlanImagePath: toStringOrUndefined(metadata["workAreaPlotPlanImagePath"]),
        marker,
        area,
        areas: areas.length > 0 ? areas : null,
        gpsLatitude: parseNullableNumber(metadata["workAreaGpsLatitude"]),
        gpsLongtitude: parseNullableNumber(metadata["workAreaGpsLongtitude"]),
        gpsAreas: normaliseGpsAreaList(metadata["workAreaGpsAreas"]),
      }

      setWorkAreaSelection(derivedWorkArea)
    }

    const selectedPermitType =
      permitTypes.find((type) => type.permitCode === permitDetail.permitTypeCode)?.permitType ||
      permitDetail.permitTypeName ||
      ""

    const generalUpdates: Partial<PermitFormState> = {}
    const extraValues: Record<string, string> = {}

    let controllerSelection: SearchSelectValue | null = null

    permitDetail.fieldValues.forEach((field) => {
      const code = field.fieldCode?.toUpperCase() ?? ""
      const sectionCode = field.sectionCode?.toUpperCase() ?? ""
      const stateKey =
        sectionCode === GENERAL_SECTION_CODE ? GENERAL_FIELD_STATE_MAP[code] : undefined
      const fieldType = (field.fieldDataType ?? "").toLowerCase()

      if (stateKey === "controller") {
        if (field.valueText) {
          const controllerLabel =
            metadata["controllerLabel"] ??
            metadata["controller"] ??
            field.valueText ??
            ""
          controllerSelection = {
            value: field.valueText,
            label: controllerLabel || field.valueText,
          }
        }
        return
      }

      if (stateKey) {
        switch (fieldType) {
          case "date":
            generalUpdates[stateKey as keyof PermitFormState] = (field.valueDate ?? "") as any
            break
          case "time": {
            const time = parseTimeValue(field.valueText)
            if (code === "STTIME") {
              generalUpdates.startTime = time
            } else if (code === "ENTIME") {
              generalUpdates.endTime = time
            } else {
              generalUpdates[stateKey as keyof PermitFormState] = (field.valueText ?? "") as any
            }
            break
          }
          case "checkbox":
          case "boolean":
            generalUpdates[stateKey as keyof PermitFormState] = (field.valueBoolean ?? false) as any
            break
          case "number":
          case "numeric":
            generalUpdates[stateKey as keyof PermitFormState] =
              field.valueNumber !== undefined && field.valueNumber !== null
                ? (String(field.valueNumber) as any)
                : ((field.valueText ?? "") as any)
            break
          default:
            generalUpdates[stateKey as keyof PermitFormState] =
              (field.valueText ??
                (field.valueBoolean !== undefined && field.valueBoolean !== null
                  ? field.valueBoolean
                  : field.valueNumber ?? field.valueDate ?? "")) as any
        }
      }

      const fallback =
        field.valueText ??
        (field.valueBoolean !== undefined && field.valueBoolean !== null
          ? String(field.valueBoolean)
          : field.valueNumber ?? field.valueDate ?? field.valueTimestamp ?? "")
      const shouldStoreInExtraValues = !stateKey && code !== "CONTROLLER"
      if (shouldStoreInExtraValues && fallback !== undefined && fallback !== null) {
        const asString = String(fallback)
        if (asString.trim().length > 0) {
          extraValues[code] = asString
          if (field.fieldId) {
            extraValues[field.fieldId] = asString
          }

          const fieldType = (field.fieldDataType ?? "").toLowerCase()
          const displayValue =
            typeof field.displayValue === "string" ? field.displayValue.trim() : ""
          if (
            displayValue &&
            (fieldType === "search_select" || fieldType === "select" || fieldType === "multiselect")
          ) {
            extraValues[buildTemplateFieldLabelKey(code)] = displayValue
            if (field.fieldId) {
              extraValues[buildTemplateFieldLabelKey(field.fieldId)] = displayValue
            }
          }
        }
      }
    })

    const selectedSubactivityIds: string[] = []
    const subactivityFieldValues: Record<string, string> = {}

    permitDetail.selectedSubActivities?.forEach((subactivity) => {
      selectedSubactivityIds.push(subactivity.subactivityId)
      subactivity.fieldValues?.forEach((field) => {
        if (!field.fieldId) {
          return
        }
        const key = buildSubactivityFieldKey(subactivity.subactivityId, field.fieldId)
        if (field.valueText !== undefined && field.valueText !== null) {
          subactivityFieldValues[key] = field.valueText
        }
      })
    })

    if (!generalUpdates.startDate) {
      const fallback = formatDateFromIso(permitDetail.plannedStart)
      if (fallback) {
        generalUpdates.startDate = fallback
      }
    }
    if (!generalUpdates.endDate) {
      const fallback = formatDateFromIso(permitDetail.plannedEnd)
      if (fallback) {
        generalUpdates.endDate = fallback
      }
    }
    if (!generalUpdates.startTime) {
      const time = formatTimeFromIso(permitDetail.plannedStart)
      if (!generalUpdates.startTime) generalUpdates.startTime = time
    }
    if (!generalUpdates.endTime) {
      const time = formatTimeFromIso(permitDetail.plannedEnd)
      if (!generalUpdates.endTime) generalUpdates.endTime = time
    }

    setPermitData((prev) => ({
      ...prev,
      permitNo: permitDetail.permitNumber || prev.permitNo,
      permitType: selectedPermitType || prev.permitType,
      permitTypeCode: permitDetail.permitTypeCode || prev.permitTypeCode,
      requestorName:
        permitDetail.requesterName ??
        metadata["requestorName"] ??
        prev.requestorName ??
        currentUser.name ??
        prev.requestorName,
      requestorCompany:
        permitDetail.requesterCompany ??
        metadata["requestorCompany"] ??
        prev.requestorCompany ??
        currentUser.company ??
        prev.requestorCompany,
      controller: controllerSelection,
      ...generalUpdates,
    }))

    setSelectedActivities(selectedSubactivityIds)

    const workerSelections: WorkerSelectionItem[] = (permitDetail.workers ?? []).map((worker) => ({
      workerId: worker.workerId,
      companyId: worker.companyId,
      companyName: resolveWorkerCompanyName(worker) ?? "",
      orgName: worker.orgName ?? null,
      fullName: worker.fullName,
      username: worker.username ?? null,
      jobRole: resolveWorkerJobRole(worker),
      status: worker.status,
      cardNumber: worker.cardNumber ?? null,
      imageType: worker.imageType ?? null,
      imagePath: worker.imagePath ?? null,
      roleInPermit: worker.roleInPermit ?? null,
    }))

    setSelectedWorkers(workerSelections)

    const combinedExtraValues: Record<string, string> = {
      ...extraValues,
      ...subactivityFieldValues,
    }

    setExtraFieldValues(combinedExtraValues)

    const attachmentItems =
      permitDetail.attachments?.map((attachment) => ({
        id: attachment.permitAttachmentId,
        docId: attachment.documentTypeId ?? attachment.documentCode ?? attachment.permitAttachmentId,
        name: attachment.fileName || attachment.documentName || "ไม่พบชื่อเอกสาร",
        size: attachment.fileSize ?? null,
        type: undefined,
        uploadedAt: attachment.uploadedAt ?? new Date().toISOString(),
        storagePath: attachment.storagePath,
        fileName: attachment.fileName || undefined,
        storedFileName: attachment.storedFileName ?? undefined,
        documentCode: attachment.documentCode ?? undefined,
        uploadedBy: attachment.uploadedBy ?? null,
        status: attachment.status ?? "ACTIVE",
      })) ?? []

    setUploadedFiles(attachmentItems)

    if (permitDetail.riskAssessment) {
      const assessment = permitDetail.riskAssessment
      setRiskData({
        consequence: {
          personnel: assessment.personScore ? String(assessment.personScore) : "",
          equipment: assessment.communityScore ? String(assessment.communityScore) : "",
          environment: assessment.environmentScore ? String(assessment.environmentScore) : "",
          property: assessment.assetScore ? String(assessment.assetScore) : "",
        },
        likelihood: {
          conditions: assessment.quantitativeScore ? String(assessment.quantitativeScore) : "",
          accident: assessment.qualitativeScore ? String(assessment.qualitativeScore) : "",
        },
      })
    } else {
      setRiskData(createEmptyRiskState())
    }

    let cancelled = false
    if (workerSelections.some((worker) => !worker.imagePath)) {
      enrichWorkersWithImages(tenantId, workerSelections)
        .then((updated) => {
          if (!cancelled) {
            setSelectedWorkers(updated)
          }
        })
        .catch(() => {
          // Swallow errors; fallback to default avatars when directory lookup fails.
        })
    }

    setHasAppliedPermitDetail(true)

    return () => {
      cancelled = true
    }
  }, [
    mode,
    permitDetail,
    templateConfig,
    permitTypes,
    hasAppliedPermitDetail,
    setPermitData,
    setExtraFieldValues,
    setSelectedActivities,
    setSelectedWorkers,
    setRiskData,
    setUploadedFiles,
    setWorkAreaSelection,
    tenantId,
    currentUser.name,
    currentUser.company,
  ])

  useEffect(() => {
    if (mode === "edit") {
      if (!hasAppliedPermitDetail) {
        return
      }
      if (permitDetail && permitDetail.permitTypeCode === templateConfig?.permitCode) {
        return
      }
    }

    // reset state when template changes for edit/new
    setSelectedActivities([])
    setExtraFieldValues({})
    setUploadedFiles([])
    setSelectedWorkers([])
    setRiskData(createEmptyRiskState())
  }, [
    mode,
    hasAppliedPermitDetail,
    permitDetail,
    templateConfig?.permitCode,
    setSelectedActivities,
    setExtraFieldValues,
    setUploadedFiles,
    setSelectedWorkers,
    setRiskData,
  ])

  return {
    permitDetail,
    isPermitDetailLoading,
    permitDetailError,
    hasAppliedPermitDetail,
    refreshPermitDetail,
    markDetailHydrationPending,
    permitDetailReloadToken,
  }
}
